import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, useClerk, useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { studentService } from '../services/studentService';

const INVITE_TOKEN_KEY = 'hypebook.schoolAdmin.inviteToken';
const INVITE_EMAIL_KEY = 'hypebook.schoolAdmin.inviteEmail';
const INVITE_SCHOOL_KEY = 'hypebook.schoolAdmin.inviteSchool';
const INVITE_NAME_KEY = 'hypebook.schoolAdmin.inviteName';
const INVITE_HAS_ACCOUNT_KEY = 'hypebook.schoolAdmin.inviteHasAccount';

interface InvitePreview {
  email: string;
  schoolName?: string | null;
  title?: string | null;
  /** Name the inviting admin entered — prefilled into the sign-up form. */
  name?: string | null;
  /** True when a Hypebook account already exists for this email — /signin shows
   *  the sign-IN surface instead of create-account (which would 'email taken'). */
  hasAccount?: boolean;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * School-admin invite landing page. Two phases:
 *
 *  1. Unauthenticated — preview the invite (school + role title), persist
 *     the token in localStorage, then bounce to /signin so Clerk can
 *     handle either sign-in or sign-up. After auth, the app-wide
 *     InviteContinuation guard (App.tsx) routes back here regardless of
 *     where Clerk redirected (it lands on /dashboard, not /signin).
 *
 *  2. Authenticated — POST the token to /v1/invites/accept (via
 *     studentService.acceptInvite), which links the User row to the
 *     SchoolMembership and flips role to SCHOOL_ADMIN if needed. On
 *     success, drop into the staff/students dashboard.
 *
 * Email prefill: this page stashes the invited email in localStorage
 * (INVITE_EMAIL_KEY) alongside the token before bouncing to /signin, which
 * passes it to Clerk's <SignIn initialValues> so the invitee just sets a
 * password. The accept step then matches the signed-in email to the invite.
 */
export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { t } = useTranslation();

  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the browser is already signed in as someone OTHER than the
  // invite recipient — see the mismatch guard below.
  const [wrongAccount, setWrongAccount] = useState<string | null>(null);
  const acceptAttemptedRef = useRef(false);

  // Persist everything /signin needs to resume this invite after auth.
  // Used both when arriving signed-out and when switching accounts.
  const stashInviteContext = useCallback(() => {
    if (!token) return;
    localStorage.setItem(INVITE_TOKEN_KEY, token);
    if (invite?.email) localStorage.setItem(INVITE_EMAIL_KEY, invite.email);
    if (invite?.schoolName) localStorage.setItem(INVITE_SCHOOL_KEY, invite.schoolName);
    if (invite?.name) localStorage.setItem(INVITE_NAME_KEY, invite.name);
    if (invite?.hasAccount) localStorage.setItem(INVITE_HAS_ACCOUNT_KEY, '1');
    else localStorage.removeItem(INVITE_HAS_ACCOUNT_KEY);
  }, [token, invite]);

  useEffect(() => {
    let mounted = true;

    const loadInvite = async () => {
      if (!token) {
        setError(t('invite.tokenMissing'));
        setLoadingPreview(false);
        return;
      }

      try {
        const data = (await studentService.previewInvite(token)) as InvitePreview;
        if (!mounted) return;
        setInvite(data);
        setError(null);
      } catch (err: unknown) {
        if (!mounted) return;
        setError(getErrorMessage(err, t('invite.unableToLoadDetails')));
      } finally {
        if (mounted) setLoadingPreview(false);
      }
    };

    loadInvite();

    return () => {
      mounted = false;
    };
  }, [token, t]);

  useEffect(() => {
    if (!token || loadingPreview || error || !isLoaded) return;

    if (!isSignedIn) {
      // Persist the invite token so it survives Clerk's sign-up flow (email
      // verification, Turnstile, sometimes a fresh tab). localStorage — not
      // sessionStorage — because sessionStorage is per-tab and is lost when
      // email-link verification opens a new tab. The app-wide
      // InviteContinuation guard resumes here once auth completes, no matter
      // which route Clerk lands the user on. Also stashes the invited email,
      // school and name so /signin can prefill the form, plus whether an
      // account already exists (sign-IN vs create surface).
      stashInviteContext();
      navigate('/signin', { replace: true });
      return;
    }

    // Wrong-account guard. Invite links are opened from email, often in a
    // browser already signed in as somebody else (e.g. an admin who just
    // invited a teacher clicking the teacher's link). Accepting would fail
    // server-side with "Signed in user email does not match invite
    // recipient" — and the old code cleared the stashed invite on that
    // failure, leaving a dead end with no way to switch accounts. Detect the
    // mismatch up front and offer to sign out and continue as the invitee.
    const inviteEmail = invite?.email?.trim().toLowerCase();
    if (inviteEmail && user) {
      // Match against ALL of the account's emails, not just the primary —
      // the invite may target a secondary address on the same account.
      const ownsInviteEmail = user.emailAddresses.some((e) => e.emailAddress.trim().toLowerCase() === inviteEmail);
      if (!ownsInviteEmail) {
        setWrongAccount(user.primaryEmailAddress?.emailAddress ?? '');
        return;
      }
    }

    if (acceptAttemptedRef.current) return;
    acceptAttemptedRef.current = true;

    const acceptInvite = async () => {
      setAccepting(true);
      try {
        await studentService.acceptInvite(token);
        navigate('/dashboard', { replace: true });
      } catch (err: unknown) {
        setError(getErrorMessage(err, t('invite.unableToAccept')));
      } finally {
        // Clear on success AND failure: a consumed-or-failed invite must not
        // keep the InviteContinuation guard bouncing the user back here.
        localStorage.removeItem(INVITE_TOKEN_KEY);
        localStorage.removeItem(INVITE_EMAIL_KEY);
        localStorage.removeItem(INVITE_SCHOOL_KEY);
        localStorage.removeItem(INVITE_NAME_KEY);
        localStorage.removeItem(INVITE_HAS_ACCOUNT_KEY);
        setAccepting(false);
      }
    };

    void acceptInvite();
  }, [token, loadingPreview, error, isLoaded, isSignedIn, navigate, t, invite, user, stashInviteContext]);

  const statusText = useMemo(() => {
    if (loadingPreview) return t('invite.loading');
    if (accepting) return t('invite.accepting');
    if (error) return t('invite.couldNotAccept');
    if (!isSignedIn) return t('invite.redirectingToSignIn');
    return t('invite.finalizing');
  }, [loadingPreview, accepting, error, isSignedIn, t]);

  // Signed in as the wrong account — offer to switch rather than dead-ending.
  // Signing out redirects back to THIS invite URL, which then takes the
  // signed-out path above (stash + /signin with the invitee's email
  // prefilled), so the invite resumes cleanly.
  if (wrongAccount) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-lg bg-white rounded-2xl p-8 border border-gray-200 shadow-sm flex flex-col gap-4">
          <h1 className="text-xl font-semibold text-gray-900">
            {t('invite.wrongAccountTitle', { defaultValue: "This invite is for a different account" })}
          </h1>
          <div className="text-sm text-gray-600 space-y-1">
            <p>
              {t('invite.wrongAccountInvited', { defaultValue: 'Invite is for' })}:{' '}
              <span className="font-medium text-gray-900">{invite?.email}</span>
            </p>
            <p>
              {t('invite.wrongAccountSignedIn', { defaultValue: "You're signed in as" })}:{' '}
              <span className="font-medium text-gray-900">{wrongAccount}</span>
            </p>
            {invite?.schoolName ? (
              <p>
                {t('invite.schoolLabel')}: <span className="font-medium text-gray-900">{invite.schoolName}</span>
              </p>
            ) : null}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                // Keep the invite context so /signin can resume + prefill.
                stashInviteContext();
                void signOut({ redirectUrl: `/invite/${token}` });
              }}
              className="px-4 py-2 bg-primary hover:bg-blue-600 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              {t('invite.wrongAccountSwitch', { defaultValue: 'Sign out and accept as {{email}}', email: invite?.email ?? '' })}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard', { replace: true })}
              className="px-4 py-2 border border-gray-300 bg-white text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('invite.wrongAccountStay', { defaultValue: 'Stay signed in as {{email}}', email: wrongAccount })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render the SAME neutral spinner as the sign-in screen for every non-error
  // state — the unauthenticated redirect hop AND the authenticated accept step
  // (preview fetch → accept POST → redirect to /dashboard). Showing the invite
  // card during the accept step made it flash on screen for a beat before the
  // dashboard loaded. The card is now shown ONLY on error, where the user
  // actually stays here for the retry CTA.
  if (!error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-gray-600">{statusText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-lg bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
        <h1 className="text-2xl font-semibold text-gray-900 mb-3">{t('invite.title')}</h1>

        {invite ? (
          <div className="space-y-1 mb-4 text-sm text-gray-600">
            <p>
              <span className="font-medium text-gray-800">{t('invite.emailLabel')}:</span> {invite.email}
            </p>
            {invite.schoolName ? (
              <p>
                <span className="font-medium text-gray-800">{t('invite.schoolLabel')}:</span> {invite.schoolName}
              </p>
            ) : null}
            {invite.title ? (
              <p>
                <span className="font-medium text-gray-800">{t('invite.roleTitleLabel')}:</span> {invite.title}
              </p>
            ) : null}
          </div>
        ) : null}

        <p className="text-gray-600 mb-4">{statusText}</p>

        {error ? (
          <div className="space-y-3">
            <p className="text-sm text-red-600">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/signin', { replace: true })}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
            >
              {t('invite.goToSignIn')}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
